import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Scene, TSceneUploadStatus, UploadStatus } from "../types/uploads";
import { ChunkedUploader } from "./ChunkedUploader";

type TFileUploadContext = {
  cancelUpload: (key: string) => void;
  cancelUploads: () => void;
  hasUploadsInProgress: boolean;
  status: TSceneUploadStatus;
  upload: (scene: Scene, onUploadComplete: () => void) => void;
};

const FileUploadContext = createContext<TFileUploadContext | null>(null);

type Props = {
  children: React.ReactNode;
}

export function FileUploadProvider({ children }: Props) {
  const [ status, setStatus ] = useState<TSceneUploadStatus>({});
  const onUnloadHandler = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  const hasUploadsInProgress = Object.values(status).some(
    ({ multiSpectral, panchromatic }) => multiSpectral === UploadStatus.Uploading || panchromatic === UploadStatus.Uploading
  );

  /**
   * Register or unregister a beforeunload handler to display a warning for ongoing uploads
   */
  useEffect(() => {
    if (hasUploadsInProgress) {
      if (!onUnloadHandler.current) {
        // Register the hander if there are uploads in progress and the hander is not yet registerd
        onUnloadHandler.current = (e: BeforeUnloadEvent) => {
          e.preventDefault();
          e.returnValue = "We're still uploading scenes in the background, which will be cancelled if you proceed. Do you want to continue.";
        };
        window.addEventListener("beforeunload", onUnloadHandler.current);

        return () => {
          onUnloadHandler.current && window.removeEventListener("beforeunload", onUnloadHandler.current);
        };
      }
    } else {
      // Unregister the hander if there are no uploads in progress and the hander has been registerd before
      if (onUnloadHandler.current) {
        window.removeEventListener("beforeunload", onUnloadHandler.current);
      }
    }
  }, [hasUploadsInProgress]);

  /**
   * Cancel a single scene upload
   */
  const cancelUpload = useCallback((key: string) => {
    if (status[key]) {
      status[key].uploadController.abort();
    }
  }, [status]);

  /**
   * Cancel all scene uploads
   */
  const cancelUploads = useCallback(() => {
    Object.values(status).map(({ uploadController }) => uploadController.abort());
    setStatus({});
  }, [status]);

  /**
   * Upload a scene
   */
  const upload = useCallback((scene: Scene, onUploadComplete: () => void) => {
    const { key, multiSpectral, panchromatic } = scene;

    const controller = new AbortController();

    const multiName = `${multiSpectral.name.split(/\.(?=[^.]+$)/)[0]}_multispectral.tif`;
    const panName = `${panchromatic.name.split(/\.(?=[^.]+$)/)[0]}_panchromatic.tif`;
    const totalSize = multiSpectral.size + panchromatic.size;

    setStatus(s => ({
      ...s,
      [key]: {
        sceneId: key,
        scene,
        multiSpectral: UploadStatus.Uploading,
        panchromatic: UploadStatus.Uploading,
        uploadController: controller,
        totalSize,
        progress: {}
      }
    }));

    const updateStatus = (fileType: string, uploadStatus: UploadStatus) => {
      setStatus(s => {
        const sceneStatus = s[key];
        return {
          ...s,
          [key]: {
            ...sceneStatus,
            [fileType]: uploadStatus,
          }
        };
      });
    };

    const handleUploadProgress = (chunkId: string, e: ProgressEvent<XMLHttpRequestEventTarget>) => {
      setStatus(s => {
        const sceneStatus = s[key];

        return {
          ...s,
          [key]: {
            ...sceneStatus,
            progress: {
              ...sceneStatus.progress,
              [chunkId]: e.loaded
            }
          }
        };
      });
    };

    Promise.all([
      new Promise<void>(resolve => {
        const uploader = new ChunkedUploader({
          file: multiSpectral,
          fileName: multiName,
          sceneId: key,
          abortSignal: controller.signal,
          chunkSize: 50,
          onError: () => updateStatus("multiSpectral", UploadStatus.Error),
          onComplete: () => {
            updateStatus("multiSpectral", UploadStatus.Success);
            resolve();
          },
          onProgress: handleUploadProgress,
        });
        uploader.start();
      }),
      new Promise<void>(resolve => {
        const uploader = new ChunkedUploader({
          file: panchromatic,
          fileName: panName,
          sceneId: key,
          abortSignal: controller.signal,
          chunkSize: 50,
          onError: () => updateStatus("panchromatic", UploadStatus.Error),
          onComplete: () => {
            updateStatus("panchromatic", UploadStatus.Success);
            resolve();
          },
          onProgress: handleUploadProgress,
        });
        uploader.start();
      }),
    ]).then(onUploadComplete);
  }, []);

  const contextValue = {
    cancelUpload,
    cancelUploads,
    hasUploadsInProgress,
    status,
    upload
  };
  return (
    <FileUploadContext.Provider value={contextValue}>
      {children}
    </FileUploadContext.Provider>
  );
}

export function useFileUpload() {
  const {
    cancelUpload,
    cancelUploads,
    hasUploadsInProgress,
    status,
    upload
  } = useContext(FileUploadContext) as TFileUploadContext;

  return useMemo(() => {
    return {
      cancelUpload,
      cancelUploads,
      hasUploadsInProgress,
      status,
      upload
    };
  }, [
    cancelUpload,
    cancelUploads,
    hasUploadsInProgress,
    status,
    upload
  ]);
}
